import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, UserPlus, FileText, Download, Calendar, Check, X, HelpCircle, Upload, Users, ShieldCheck } from "lucide-react";
import api, { API, formatApiErrorDetail, isPlatinumRequired } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { localYmd } from "../lib/utils";
import { toast } from "sonner";

export default function EnsembleDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [ens, setEns] = useState(null);
  const [tab, setTab] = useState("overview");
  const [rehearsals, setRehearsals] = useState([]);
  const [docs, setDocs] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [showRehearsal, setShowRehearsal] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/ensembles/${id}`);
      setEns(data);
      const [r, d] = await Promise.all([
        api.get(`/ensembles/${id}/rehearsals`),
        api.get(`/ensembles/${id}/documents`),
      ]);
      setRehearsals(r.data || []);
      setDocs(d.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!ens) return <div className="text-ink-500">Loading…</div>;
  const isManager = ens.my_role === "owner" || ens.my_role === "manager";

  return (
    <div className="space-y-5" data-testid="ensemble-detail">
      <Link to="/ensembles" className="btn-ghost text-sm w-fit" data-testid="back-to-ensembles"><ArrowLeft size={16} /> All ensembles</Link>

      <div className="card-base p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="label-overline">Ensemble</div>
            <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">{ens.name}</h1>
            {ens.location && <div className="text-sm text-ink-500 mt-1">{ens.location}</div>}
          </div>
          <span className="pill bg-sage-100 text-sage-700 capitalize"><ShieldCheck size={12} /> {ens.my_role}</span>
        </div>
        {ens.description && <p className="mt-3 text-ink-700 leading-relaxed">{ens.description}</p>}
      </div>

      <div className="flex gap-1 bg-ivory-100 rounded-full p-1 w-fit">
        {[
          { k: "overview", l: "Overview" },
          { k: "rehearsals", l: "Rehearsals" },
          { k: "documents", l: "Documents" },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`rounded-full px-4 py-1.5 text-sm font-medium ${tab === t.k ? "bg-white shadow-soft text-ink-900" : "text-ink-600"}`} data-testid={`ens-tab-${t.k}`}>{t.l}</button>
        ))}
      </div>

      {tab === "overview" && (
        <section className="card-base p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading font-semibold inline-flex items-center gap-2"><Users size={16} className="text-sage-500" /> Members ({ens.members.length})</h2>
            {isManager && <button onClick={() => setShowInvite(true)} className="btn-secondary text-sm" data-testid="invite-member-btn"><UserPlus size={16} /> Invite</button>}
          </div>
          <ul className="divide-y divide-ivory-200">
            {ens.members.map((m) => (
              <li key={m.user_id} className="py-3 flex items-center justify-between" data-testid={`member-${m.user_id}`}>
                <div>
                  <div className="font-medium">{m.user_name}</div>
                  <div className="text-xs text-ink-500">{m.instrument || "—"} · {m.role}</div>
                </div>
                {isManager && m.user_id !== ens.owner_id && (
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Remove ${m.user_name}?`)) return;
                      try { await api.delete(`/ensembles/${id}/members/${m.user_id}`); load(); }
                      catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
                    }}
                    className="btn-ghost text-terracotta-600" data-testid={`remove-member-${m.user_id}`}
                  ><Trash2 size={14} /></button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {tab === "rehearsals" && (
        <section className="space-y-3">
          <div className="flex justify-end">
            {isManager && <button onClick={() => setShowRehearsal(true)} className="btn-primary text-sm" data-testid="add-rehearsal-btn"><Plus size={16} /> Add rehearsal</button>}
          </div>
          {rehearsals.length === 0 ? (
            <div className="card-base p-6 text-ink-600" data-testid="rehearsals-empty">No rehearsals scheduled.</div>
          ) : (
            <ul className="space-y-2">
              {rehearsals.map((r) => (
                <RehearsalCard key={r.id} rehearsal={r} ensembleId={id} onChange={load} canManage={isManager} />
              ))}
            </ul>
          )}
        </section>
      )}

      {tab === "documents" && (
        <DocumentsTab id={id} docs={docs} onChange={load} canDelete={(d) => isManager || d.owner_id === user.id} />
      )}

      {showInvite && <InviteModal id={id} onClose={() => { setShowInvite(false); load(); }} />}
      {showRehearsal && <RehearsalModal id={id} onClose={() => { setShowRehearsal(false); load(); }} />}
    </div>
  );
}

function RehearsalCard({ rehearsal: r, ensembleId, onChange, canManage }) {
  const respond = async (status) => {
    try {
      await api.post(`/rehearsals/${r.id}/attendance`, { status });
      onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const remove = async () => {
    if (!window.confirm("Delete rehearsal?")) return;
    await api.delete(`/ensembles/${ensembleId}/rehearsals/${r.id}`);
    onChange();
  };
  return (
    <li className="card-base p-4" data-testid={`rehearsal-${r.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs label-overline text-ink-500">{new Date(r.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}</div>
          <div className="font-heading font-semibold mt-1">{r.title}</div>
          <div className="text-sm text-ink-600">
            {new Date(r.start_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
            {" – "}
            {new Date(r.end_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
            {r.venue ? ` · ${r.venue}` : ""}
          </div>
          {r.repertoire && <div className="text-xs text-ink-500 mt-0.5">{r.repertoire}</div>}
        </div>
        {canManage && (
          <button onClick={remove} className="btn-ghost text-terracotta-600" data-testid={`rehearsal-delete-${r.id}`}><Trash2 size={14} /></button>
        )}
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2">
        <AttendBtn active={r.my_status === "going"} onClick={() => respond("going")} variant="primary" testid={`reh-going-${r.id}`}><Check size={14} /> Going · {r.attendance_summary?.going || 0}</AttendBtn>
        <AttendBtn active={r.my_status === "tentative"} onClick={() => respond("tentative")} variant="secondary" testid={`reh-tentative-${r.id}`}><HelpCircle size={14} /> Maybe · {r.attendance_summary?.tentative || 0}</AttendBtn>
        <AttendBtn active={r.my_status === "not_going"} onClick={() => respond("not_going")} variant="terracotta" testid={`reh-no-${r.id}`}><X size={14} /> Can't · {r.attendance_summary?.not_going || 0}</AttendBtn>
      </div>
    </li>
  );
}

function AttendBtn({ active, onClick, variant, children, testid }) {
  const cls = active
    ? variant === "terracotta"
      ? "bg-terracotta-500 text-white"
      : variant === "secondary"
      ? "bg-amber-soft text-amber-deep border border-amber-deep/30"
      : "bg-sage-500 text-white"
    : "bg-ivory-100 text-ink-700 hover:bg-ivory-200";
  return (
    <button onClick={onClick} data-testid={testid} className={`rounded-full text-xs font-medium py-2 inline-flex items-center justify-center gap-1.5 transition ${cls}`}>
      {children}
    </button>
  );
}

function InviteModal({ id, onClose }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/ensembles/${id}/invites`, { email, role });
      toast.success("Member added");
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-ink-900/40 p-2" onClick={onClose} data-testid="invite-modal">
      <div className="bg-white w-full max-w-md rounded-2xl sm:rounded-3xl p-6 shadow-card" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl font-semibold">Invite member</h3>
          <button onClick={onClose} className="btn-ghost"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <input className="input-field" type="email" required placeholder="member@email.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invite-email-input" />
          <select className="input-field" value={role} onChange={(e) => setRole(e.target.value)} data-testid="invite-role-select">
            <option value="member">Member</option>
            <option value="section_leader">Section leader</option>
            <option value="manager">Manager</option>
          </select>
          <p className="text-xs text-ink-500">The member must already have an AMDS account. Real email invites are coming soon.</p>
          <button disabled={busy} className="btn-primary w-full justify-center" data-testid="invite-submit-btn">{busy ? "Adding…" : "Add member"}</button>
        </form>
      </div>
    </div>
  );
}

function RehearsalModal({ id, onClose }) {
  const today = localYmd();
  const [form, setForm] = useState({ title: "", date: today, start_time: "19:30", end_time: "21:30", venue: "", repertoire: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        title: form.title, date: form.date,
        start_at: new Date(`${form.date}T${form.start_time}:00`).toISOString(),
        end_at: new Date(`${form.date}T${form.end_time}:00`).toISOString(),
        venue: form.venue, repertoire: form.repertoire, notes: form.notes,
      };
      await api.post(`/ensembles/${id}/rehearsals`, payload);
      toast.success("Rehearsal added");
      onClose();
    } catch (err) {
      if (isPlatinumRequired(err)) {
        toast.error("Upgrade to Platinum to schedule unlimited rehearsals");
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-ink-900/40 p-2" onClick={onClose} data-testid="rehearsal-modal">
      <div className="bg-white w-full max-w-md rounded-2xl sm:rounded-3xl p-6 shadow-card max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl font-semibold">New rehearsal</h3>
          <button onClick={onClose} className="btn-ghost"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <input required className="input-field" placeholder="Title (e.g. Mahler 5 tutti)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="rehearsal-title-input" />
          <input required type="date" className="input-field" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="rehearsal-date-input" />
          <div className="grid grid-cols-2 gap-2">
            <input required type="time" className="input-field" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="rehearsal-start-input" />
            <input required type="time" className="input-field" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="rehearsal-end-input" />
          </div>
          <input className="input-field" placeholder="Venue" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} data-testid="rehearsal-venue-input" />
          <input className="input-field" placeholder="Repertoire" value={form.repertoire} onChange={(e) => setForm({ ...form, repertoire: e.target.value })} data-testid="rehearsal-rep-input" />
          <textarea rows={2} className="input-field" placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="rehearsal-notes-input" />
          <button disabled={busy} className="btn-primary w-full justify-center" data-testid="rehearsal-submit-btn">{busy ? "Saving…" : "Save"}</button>
        </form>
      </div>
    </div>
  );
}

function DocumentsTab({ id, docs, onChange, canDelete }) {
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/ensembles/${id}/documents`, {
        method: "POST", credentials: "include", body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        if (res.status === 402) {
          toast.error("Document uploads are Platinum-only. Visit Pricing to upgrade.");
          return;
        }
        throw new Error(err.detail?.message || err.detail || "Upload failed");
      }
      toast.success("Uploaded");
      onChange();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete ${doc.original_filename}?`)) return;
    await api.delete(`/ensembles/${id}/documents/${doc.id}`);
    onChange();
  };

  const download = async (doc) => {
    const res = await fetch(`${API}/files/${doc.storage_path}`, { credentials: "include" });
    if (!res.ok) { toast.error("Download failed"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = doc.original_filename || "amds-document";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <section className="space-y-3">
      <div className="flex justify-end">
        <label className="btn-primary text-sm cursor-pointer" data-testid="upload-doc-label">
          <Upload size={16} /> {busy ? "Uploading…" : "Upload document"}
          <input type="file" hidden onChange={upload} accept=".pdf,.docx,.doc,.xlsx,.png,.jpg,.jpeg,.webp,.txt" data-testid="upload-doc-input" />
        </label>
      </div>
      {docs.length === 0 ? (
        <div className="card-base p-6 text-ink-600" data-testid="documents-empty">No documents yet.</div>
      ) : (
        <ul className="space-y-2">
          {docs.map((d) => (
            <li key={d.id} className="card-base p-4 flex items-center justify-between gap-3" data-testid={`doc-${d.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-sage-100 text-sage-600 grid place-items-center"><FileText size={18} /></div>
                <div className="min-w-0">
                  <div className="font-medium truncate">{d.original_filename}</div>
                  <div className="text-xs text-ink-500">{(d.size / 1024).toFixed(1)} KB · {new Date(d.created_at).toLocaleDateString()} · by {d.owner_name}</div>
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => download(d)} className="btn-ghost" data-testid={`doc-download-${d.id}`}><Download size={16} /></button>
                {canDelete(d) && <button onClick={() => remove(d)} className="btn-ghost text-terracotta-600" data-testid={`doc-delete-${d.id}`}><Trash2 size={16} /></button>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
